import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useReactivateUser } from "./use-reactivate-user";
import type { UserDetail } from "./types";

interface ReactivateUserDialogProps {
  readonly user: UserDetail | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

export function ReactivateUserDialog({
  user,
  open,
  onOpenChange,
  onSuccess,
}: ReactivateUserDialogProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const mutation = useReactivateUser();

  useEffect(() => {
    if (open) setError(null);
  }, [open, user?.id]);

  if (!user) return null;

  const handleOpenChange = (next: boolean) => {
    if (!next && mutation.isPending) return;
    onOpenChange(next);
  };

  const handleConfirm = () => {
    setError(null);
    mutation.mutate(user.id, {
      onSuccess: () => {
        onOpenChange(false);
        onSuccess();
      },
      onError: (err) => {
        setError(
          resolveMutationErrorMessage(err, t, {
            forbiddenKey: "users.errors.forbidden",
            serverPrefixKey: "users.errors.serverPrefix",
            genericKey: "users.reactivate.error",
          }),
        );
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="reactivate-user-dialog">
        <DialogHeader>
          <DialogTitle>{t("users.reactivate.title")}</DialogTitle>
          <DialogDescription>
            {t("users.reactivate.description", {
              name: user.displayName || user.email,
            })}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p
            data-testid="reactivate-user-error"
            className="font-sans text-sm font-medium text-destructive"
          >
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t("users.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-reactivate-user-button"
            onClick={handleConfirm}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t("users.reactivate.pending") : t("users.reactivate.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
